import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import { Flag, Mail, Send, RefreshCcw, Loader2 } from 'lucide-react';
import { syncSprintResults, sendSprintRecalcEmail } from '../../services/api';

const SyncReport = ({ races }) => (
  <div className="mt-3 space-y-2" data-testid="sprint-sync-report">
    {races.map((r) => (
      <div key={r.race_id} className="rounded border p-2 text-sm" data-testid={`sprint-sync-row-${r.race_id}`}>
        <div className="font-semibold">{r.name}</div>
        <div className="text-muted-foreground">
          Winnaar: {r.current?.sprint_winner || '-'} · Pole: {r.current?.sprint_pole_winner || '-'} · Tijd: {r.current?.sprint_pole_time || '-'}
          {Object.keys(r.fetched).length > 0 && <span className="text-blue-600"> (nieuw ingevuld: {Object.keys(r.fetched).length})</span>}
        </div>
        <div className={r.rescored || (!r.updated && !r.conflicts?.length && Object.keys(r.current || {}).length) ? 'text-green-600' : 'text-orange-600'}>
          {r.rescored ? 'Punten herberekend' : (r.error || (r.updated ? 'Opgeslagen' : (Object.keys(r.current || {}).length ? 'Al ingevuld' : 'Geen gegevens gevonden')))}
          {r.missing.length > 0 && ` · Ontbreekt: ${r.missing.join(', ')} (handmatig invullen)`}
        </div>
        {r.conflicts?.length > 0 && (
          <div className="text-red-600" data-testid={`sprint-sync-conflict-${r.race_id}`}>
            Niet overschreven (controleer): {r.conflicts.map((c) => `${c.field}: opgeslagen ${c.saved}, bron ${c.fetched}`).join(' · ')}
          </div>
        )}
      </div>
    ))}
  </div>
);

export const SprintRecoveryCard = () => {
  const { toast } = useToast();
  const [busy, setBusy] = useState(null);
  const [report, setReport] = useState(null);
  const [note, setNote] = useState('');
  const [testSent, setTestSent] = useState(false);

  const run = async (key, fn, onDone) => {
    setBusy(key);
    try {
      const result = await fn();
      onDone?.(result);
      toast({ title: 'Gelukt', description: result.message });
    } catch (error) {
      toast({ title: 'Fout', description: error.response?.data?.detail || error.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const sendAll = () => {
    if (!window.confirm('E-mail nu versturen naar alle actieve deelnemers?')) return;
    run('all', () => sendSprintRecalcEmail(note, false));
  };

  return (
    <Card className="border-2 border-[#FDB913] mb-6" data-testid="sprint-recovery-card">
      <CardHeader>
        <CardTitle className="flex items-center"><Flag className="mr-2 text-[#FDB913]" size={20} />Sprintpunten Herstellen</CardTitle>
        <CardDescription>Haal de sprintuitslagen van afgelopen sprintweekenden op, herbereken de punten en informeer de deelnemers.</CardDescription>
      </CardHeader>
      <CardContent>
        <Button onClick={() => run('sync', syncSprintResults, (r) => setReport(r.races))} disabled={!!busy} variant="outline" className="border-blue-500 text-blue-600 hover:bg-blue-50" data-testid="sync-sprint-results-btn">
          {busy === 'sync' ? <Loader2 className="mr-2 animate-spin" size={16} /> : <RefreshCcw className="mr-2" size={16} />}
          1. Sprintuitslagen Ophalen &amp; Herberekenen
        </Button>
        <p className="text-xs text-muted-foreground mt-1">Haalt sprintwinnaar, sprint pole en pole tijd op en scoort de races opnieuw. Controleer de uitslag hieronder (sprint pole kan afwijken bij een gridstraf); aanpassen kan via Resultaten.</p>
        {report && <SyncReport races={report} />}

        <div className="mt-6">
          <Label htmlFor="sprint-email-note" className="font-semibold">Eigen toelichting (optioneel)</Label>
          <Textarea id="sprint-email-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1" placeholder="Bijv. excuses voor het ongemak..." data-testid="sprint-email-note-input" />
          <p className="text-xs text-muted-foreground mt-1">De e-mail bevat een vaste uitleg, jouw toelichting en per deelnemer de sprintpunten, het totaal en de positie.</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={() => run('test', () => sendSprintRecalcEmail(note, true), () => setTestSent(true))} disabled={!!busy} variant="outline" data-testid="send-sprint-email-test-btn">
              {busy === 'test' ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Mail className="mr-2" size={16} />}
              2. Test naar mij
            </Button>
            <Button onClick={sendAll} disabled={!!busy || !testSent} className="bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a60f]" data-testid="send-sprint-email-all-btn">
              {busy === 'all' ? <Loader2 className="mr-2 animate-spin" size={16} /> : <Send className="mr-2" size={16} />}
              3. Verstuur naar alle deelnemers
            </Button>
          </div>
          {!testSent && <p className="text-xs text-muted-foreground mt-1">Stuur eerst een test naar jezelf om het versturen naar iedereen te ontgrendelen.</p>}
        </div>
      </CardContent>
    </Card>
  );
};
